import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AccountSettingsPanel.css";
import { AUTH_BASE_URL, buildApiUrl, ensureArray, getApiHeaders } from "./config";
import { getAuthProfileFallback, getAuthToken } from "./auth/authUtils";
import SubscriptionRequestStatus from "./SubscriptionRequestStatus";
const defaultForm = (workspace) => ({
  exchange: workspace?.exchange || "",
  marketType: workspace?.marketType || "",
  accountLabel: "",
  apiKey: "",
  apiSecret: "",
  passphrase: "",
});



function AccountSettingsPanel({ workspace, scopeOptions = [], onClose }) {
  const [profile, setProfile] = useState(() => getAuthProfileFallback());
  const [planContext, setPlanContext] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [form, setForm] = useState(defaultForm(workspace));
  const [editingKey, setEditingKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = getAuthToken();

  const authHeaders = useMemo(
    () =>
      getApiHeaders({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }),
    [token]
  );

  const loadData = useCallback(async () => {
    if (!token) {
      setError("You need to be logged in to manage account settings.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const fallbackProfile = getAuthProfileFallback();
      if (fallbackProfile) {
        setProfile(fallbackProfile);
      }

      const profileRes = await fetch(`${AUTH_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!profileRes.ok) {
        throw new Error(await profileRes.text());
      }

      const profileJson = await profileRes.json();
      setProfile(profileJson);

      const [planResult, credentialResult] = await Promise.allSettled([
        fetch(buildApiUrl("/trade/api/v2/plan/context"), {
          headers: authHeaders,
        }),
        fetch(buildApiUrl("/trade/api/v2/credentials"), {
          headers: authHeaders,
        }),
      ]);

      if (planResult.status === "fulfilled" && planResult.value.ok) {
        setPlanContext(await planResult.value.json());
      } else {
        setPlanContext(null);
      }

      if (credentialResult.status === "fulfilled" && credentialResult.value.ok) {
        setCredentials(ensureArray(await credentialResult.value.json()));
      } else {
        setCredentials([]);
      }

      const optionalLoadFailed =
        planResult.status === "rejected" ||
        credentialResult.status === "rejected" ||
        (planResult.status === "fulfilled" && !planResult.value.ok) ||
        (credentialResult.status === "fulfilled" && !credentialResult.value.ok);

      if (optionalLoadFailed) {
        setError("Profile loaded. Subscription context or credentials could not be loaded right now.");
      }
    } catch (fetchError) {
      const fallbackProfile = getAuthProfileFallback();
      if (fallbackProfile) {
        setProfile(fallbackProfile);
        setPlanContext(null);
        setCredentials([]);
        setError("Showing saved login profile. Live account settings could not be loaded right now.");
      } else {
        setError(fetchError.message || "Unable to load account settings right now.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = useCallback(() => {
    setEditingKey(null);
    setForm(defaultForm(workspace));
  }, [workspace]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleEdit = (credential) => {
    setEditingKey(`${credential.exchange}:${credential.marketType}`);
    setForm({
      exchange: credential.exchange,
      marketType: credential.marketType,
      accountLabel: credential.accountLabel || "",
      apiKey: "",
      apiSecret: "",
      passphrase: "",
    });
    setSuccess("");
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    const isEditing = Boolean(editingKey);
    const requestPath = isEditing ? "/trade/api/v2/credentials" : "/trade/api/v2/credentials";
    const requestMethod = isEditing ? "PUT" : "POST";

    const payload = {
      exchange: form.exchange,
      marketType: form.marketType,
      accountLabel: form.accountLabel.trim() || null,
    };

    if (isEditing) {
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (form.apiSecret.trim()) payload.apiSecret = form.apiSecret.trim();
      if (form.passphrase !== "") payload.passphrase = form.passphrase;
    } else {
      payload.apiKey = form.apiKey.trim();
      payload.apiSecret = form.apiSecret.trim();
      payload.passphrase = form.passphrase.trim() || null;
    }

    try {
      const response = await fetch(buildApiUrl(requestPath), {
        method: requestMethod,
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setSuccess(
        isEditing
          ? "Exchange credentials updated."
          : "Exchange credentials saved."
      );
      resetForm();
      await loadData();
    } catch (submitError) {
      setError(submitError.message || "Unable to save credentials.");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (credential) => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(buildApiUrl("/trade/api/v2/credentials/revoke"), {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          exchange: credential.exchange,
          marketType: credential.marketType,
          reason: "USER_REQUESTED",
        }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setSuccess("Exchange credentials disabled.");
      if (editingKey === `${credential.exchange}:${credential.marketType}`) {
        resetForm();
      }
      await loadData();
    } catch (revokeError) {
      setError(revokeError.message || "Unable to disable credentials.");
    } finally {
      setSaving(false);
    }
  };

  const allowedExchanges = planContext?.limits?.allowedExchanges || [];
  const allowedMarketTypes = planContext?.limits?.allowedMarketTypes || [];

  return (
    <div className="account-settings-shell">
      <div className="account-settings-panel">
        <div className="account-settings-panel__header">
          <div>
            <h2>Account & Exchange Settings</h2>
            <p>Manage your subscription context and the exchange credentials trading uses.</p>
          </div>
          <button className="header-btn" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <div className="account-settings-state">Loading account settings...</div>
        ) : (
          <>
            {error && <div className="account-settings-alert account-settings-alert--error">{error}</div>}
            {success && <div className="account-settings-alert account-settings-alert--success">{success}</div>}

            <section className="account-settings-grid">
              <article className="account-settings-card">
                <h3>Identity</h3>
                <div className="account-settings-field">
                  <span>Email</span>
                  <strong>{profile?.email || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>User ID</span>
                  <strong>{profile?.userId || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Public User ID</span>
                  <strong>{profile?.publicUserId || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Status</span>
                  <strong>{profile?.status || "-"}</strong>
                </div>
              </article>

              <article className="account-settings-card">
                <h3>Subscription</h3>
                <div className="account-settings-field">
                  <span>Plan</span>
                  <strong>{profile?.plan || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Subscription Active</span>
                  <strong>{String(profile?.subscriptionActive ?? false)}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Expiry</span>
                  <strong>{profile?.subscriptionExpiry || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Allowed Exchanges</span>
                  <strong>{allowedExchanges.join(", ") || "-"}</strong>
                </div>
                <div className="account-settings-field">
                  <span>Allowed Market Types</span>
                  <strong>{allowedMarketTypes.join(", ") || "-"}</strong>
                </div>
                <SubscriptionRequestStatus />
              </article>
            </section>

            <section className="account-settings-card">
              <div className="account-settings-card__title-row">
                <h3>{editingKey ? "Update Exchange Credential" : "Add Exchange Credential"}</h3>
                {editingKey && (
                  <button className="header-btn" onClick={resetForm} type="button">
                    Cancel edit
                  </button>
                )}
              </div>

              <form className="account-settings-form" onSubmit={handleSubmit}>
                <label>
                  <span>Exchange</span>
                  <select
                    name="exchange"
                    value={form.exchange}
                    onChange={(event) => {
                      const exchange = event.target.value;
                      const marketType = scopeOptions.find(
                        option => option.exchange === exchange)?.marketType || "";
                      setForm(current => ({ ...current, exchange, marketType }));
                    }}
                    disabled={saving || Boolean(editingKey)}
                  >
                    {[...new Set(scopeOptions.map(option => option.exchange))].map((exchange) => (
                      <option key={exchange} value={exchange}>
                        {exchange}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Market Type</span>
                  <select
                    name="marketType"
                    value={form.marketType}
                    onChange={handleChange}
                    disabled={saving || Boolean(editingKey)}
                  >
                    {[...new Set(scopeOptions
                      .filter(option => option.exchange === form.exchange)
                      .map(option => option.marketType))].map((marketType) => (
                      <option key={marketType} value={marketType}>
                        {marketType}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Account Label</span>
                  <input
                    name="accountLabel"
                    value={form.accountLabel}
                    onChange={handleChange}
                    placeholder="Primary Binance Perps"
                  />
                </label>

                <label>
                  <span>API Key</span>
                  <input
                    name="apiKey"
                    value={form.apiKey}
                    onChange={handleChange}
                    placeholder={editingKey ? "Leave blank to keep existing" : "Enter API key"}
                    required={!editingKey}
                  />
                </label>

                <label>
                  <span>Secret Key</span>
                  <input
                    name="apiSecret"
                    value={form.apiSecret}
                    onChange={handleChange}
                    placeholder={editingKey ? "Leave blank to keep existing" : "Enter API secret"}
                    required={!editingKey}
                  />
                </label>

                <label>
                  <span>Passphrase</span>
                  <input
                    name="passphrase"
                    value={form.passphrase}
                    onChange={handleChange}
                    placeholder="Optional for exchanges that require it"
                  />
                </label>

                <div className="account-settings-form__actions">
                  <button className="header-btn header-btn--admin" type="submit" disabled={saving}>
                    {saving ? "Saving..." : editingKey ? "Update Credential" : "Save Credential"}
                  </button>
                </div>
              </form>
            </section>

            <section className="account-settings-card">
              <h3>Connected Exchange Credentials</h3>
              {credentials.length === 0 ? (
                <div className="account-settings-state">
                  No exchange credentials saved yet.
                </div>
              ) : (
                <div className="account-settings-credential-list">
                  {credentials.map((credential) => {
                    const credentialKey = `${credential.exchange}:${credential.marketType}`;
                    return (
                      <article key={credentialKey} className="account-settings-credential-item">
                        <div>
                          <div className="account-settings-credential-item__title">
                            <strong>{credential.exchange} / {credential.marketType}</strong>
                            {credential.accountLabel && <span>{credential.accountLabel}</span>}
                          </div>
                          <div className="account-settings-credential-item__meta">
                            <span>API Key: {credential.apiKeyMasked}</span>
                            <span>Passphrase: {credential.passphraseConfigured ? "Configured" : "Not set"}</span>
                            <span>Status: {credential.status}</span>
                          </div>
                        </div>
                        <div className="account-settings-credential-item__actions">
                          <button className="header-btn" type="button" onClick={() => handleEdit(credential)}>
                            Edit
                          </button>
                          {credential.status === "ACTIVE" && (
                            <button
                              className="header-btn header-btn-danger"
                              type="button"
                              onClick={() => handleRevoke(credential)}
                              disabled={saving}
                            >
                              Disable
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default AccountSettingsPanel;
